import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  Checkbox,
  Tabs,
  Tab,
  Tooltip,
} from "@material-ui/core";
import { Rating } from "@material-ui/lab";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
} from "@material-ui/icons";
import { DataPulseContext } from "../contexts/DataPulseContext";
import { NotificationContext } from "../contexts/NotificationContext";
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    minHeight: "100vh",
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  questionCard: {
    marginBottom: theme.spacing(2),
    border: "2px solid transparent",
    "&:hover": {
      border: "2px solid #1976d2",
    },
  },
  questionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  dragHandle: {
    cursor: "grab",
    marginRight: theme.spacing(1),
  },
  option: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  previewSection: {
    backgroundColor: "#fff",
    padding: theme.spacing(3),
    borderRadius: 8,
  },
}));

const questionTypes = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "checkboxes", label: "Checkboxes" },
  { value: "dropdown", label: "Dropdown" },
  { value: "rating", label: "Rating Scale" },
  { value: "yes_no", label: "Yes/No" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone Number" },
  { value: "date", label: "Date" },
];

const CreateSurvey = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { createSurvey, updateSurvey, loadSurvey, currentSurvey, addQuestion, updateQuestion, checkQuestionLocks, deleteQuestion, getSurveyPreviewHtmlUrl, buildLivePreviewHtml, postSurveyLivePreview } = useContext(DataPulseContext);
  const { notify } = useContext(NotificationContext);

  const [tabValue, setTabValue] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sidePreviewHtml, setSidePreviewHtml] = useState("");
  const [isServerPreview, setIsServerPreview] = useState(false);
  const livePreviewTimerRef = useRef(null);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [previewVisibleMobile, setPreviewVisibleMobile] = useState(false);
  const [questionLocks, setQuestionLocks] = useState({}); // { questionId: { locked, answer_count } }
  const [optionLocks, setOptionLocks] = useState({}); // { optionId: { locked, usage_count } }
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockDialogInfo, setLockDialogInfo] = useState(null);
  const [survey, setSurvey] = useState({
    name: "",
    description: "",
    welcome_message: "",
    thank_you_message: "",
    allow_anonymous: true,
    status: "draft",
    nonce_expiration_hours: 168, // Default to 7 days
  });

  const [questions, setQuestions] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingMode, setSavingMode] = useState(null); // 'draft' | 'publish' | null
  const [isDirty, setIsDirty] = useState(false);
  const originalQuestionIdsRef = useRef([]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty && !isSaving) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    let unblock = null;
    if (isDirty && !isSaving) {
      unblock = history.block('You have unsaved changes. Are you sure you want to leave this page?');
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (unblock) unblock();
    };
  }, [isDirty, isSaving, history]);

  useEffect(() => {
    if (id) {
      loadSurvey(id);
    }
  }, [id]);

  useEffect(() => {
    if (currentSurvey && id) {
      setSurvey({
        name: currentSurvey.name,
        description: currentSurvey.description,
        welcome_message: currentSurvey.welcome_message || "",
        thank_you_message: currentSurvey.thank_you_message || "",
        allow_anonymous: currentSurvey.allow_anonymous,
        status: currentSurvey.status || "draft",
        nonce_expiration_hours: currentSurvey.nonce_expiration_hours || 168, // Default to 7 days
      });
      // Normalize question fields from backend to editor shape
      const normalizedQs = (currentSurvey.questions || []).map((q, idx) => ({
        ...q,
        // Map backend placeholder_text -> UI placeholder so editor fields and client preview show correct value
        placeholder: q.placeholder || q.placeholder_text || "",
        display_order: q.display_order || idx + 1,
      }));
      setQuestions(normalizedQs);
      // Track original persistent question IDs for deletion diff
      originalQuestionIdsRef.current = (currentSurvey.questions || [])
        .map((q) => q.id)
        .filter((qid) => qid && !qid.toString().startsWith("temp-"));
      setIsDirty(false);
      
      // Load question locks (which questions have answers)
      loadQuestionLocks();
    }
  }, [currentSurvey, id]);

  // Load question lock status
  const loadQuestionLocks = async () => {
    if (!id) return;
    try {
      const locks = await checkQuestionLocks(id);
      
      // Handle both old format (array) and new format (object with questions/options)
      const questionData = locks.questions || locks;
      const optionData = locks.options || [];
      
      // Convert question array to map for easy lookup
      const lockMap = {};
      questionData.forEach(lock => {
        lockMap[lock.question_id] = {
          locked: lock.locked,
          answer_count: lock.answer_count,
          question_text: lock.question_text,
          question_type: lock.question_type
        };
      });
      
      // Convert option array to map { optionId: { locked, usage_count } }
      const optionLockMap = {};
      optionData.forEach(opt => {
        optionLockMap[opt.option_id] = {
          locked: opt.locked,
          usage_count: opt.usage_count
        };
      });
      
      setQuestionLocks(lockMap);
      setOptionLocks(optionLockMap);
    } catch (error) {
      console.error('Error loading question locks:', error);
    }
  };

  const handleSurveyChange = (field, value) => {
    setSurvey({ ...survey, [field]: value });
    setIsDirty(true);
    scheduleLivePreview();
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `temp-${Date.now()}`,
      question_text: "",
      question_type: "text",
      is_required: false,
      display_order: questions.length + 1,
      options: [],
      settings: {},
    };
    setQuestions([...questions, newQuestion]);
    setIsDirty(true);
    scheduleLivePreview();
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index][field] = value;
    setQuestions(updatedQuestions);
    setIsDirty(true);
    scheduleLivePreview();
  };

  const handleDeleteQuestion = async (index) => {
    const question = questions[index];
    
    // Check if question is locked (has answers)
    const lockInfo = questionLocks[question.id];
    if (lockInfo && lockInfo.locked) {
      // Show friendly dialog instead of trying to delete
      setLockDialogInfo({
        question_text: question.question_text,
        answer_count: lockInfo.answer_count
      });
      setLockDialogOpen(true);
      return;
    }
    
    // Question is not locked - proceed with deletion
    // If this is a saved question (has real ID), delete from backend
    if (question.id && !question.id.toString().startsWith('temp-')) {
      try {
        await deleteQuestion(question.id);
        // Success - remove from originalQuestionIds so we don't try to delete again on save
        originalQuestionIdsRef.current = originalQuestionIdsRef.current.filter(id => id !== question.id);
      } catch (error) {
        // Unexpected error (shouldn't happen since we checked locks)
        const errorData = error.response?.data;
        notify(errorData?.error || 'Failed to delete question', 'error');
        return;
      }
    }
    
    // Remove from local state
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
    setIsDirty(true);
    
    // Immediate preview refresh: if no questions left, clear HTML so stale content disappears
    if (updatedQuestions.length === 0) {
      // Build empty preview using current survey data (will show welcome if present, no questions)
      try {
        if (id) {
          // For saved surveys call live preview endpoint with empty questions list
          postSurveyLivePreview({
            name: survey.name,
            description: survey.description,
            welcome_message: survey.welcome_message,
            thank_you_message: survey.thank_you_message,
          }, [])
            .then(html => setSidePreviewHtml(html))
            .catch(() => setSidePreviewHtml('<p style="padding:16px;font-family:sans-serif;color:#9e9e9e">No questions</p>'));
        } else {
          setSidePreviewHtml(buildLivePreviewHtml(survey, []));
        }
      } catch (e) {
        console.error('Preview refresh error after last deletion:', e);
        setSidePreviewHtml('<p style="padding:16px;font-family:sans-serif;color:#9e9e9e">No questions</p>');
      }
    } else {
      scheduleLivePreview();
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedQuestions = [...questions];
    const draggedQuestion = updatedQuestions[draggedIndex];
    updatedQuestions.splice(draggedIndex, 1);
    updatedQuestions.splice(index, 0, draggedQuestion);
    
    setQuestions(updatedQuestions);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setIsDirty(true);
  };

  const handleAddOption = (questionIndex) => {
    const updatedQuestions = [...questions];
    if (!updatedQuestions[questionIndex].options) {
      updatedQuestions[questionIndex].options = [];
    }
    updatedQuestions[questionIndex].options.push({
      id: `temp-opt-${Date.now()}`,
      option_text: "",
      display_order: updatedQuestions[questionIndex].options.length + 1,
    });
    setQuestions(updatedQuestions);
    setIsDirty(true);
    scheduleLivePreview();
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex].option_text = value;
    setQuestions(updatedQuestions);
    setIsDirty(true);
    scheduleLivePreview();
  };

  const handleDeleteOption = (questionIndex, optionIndex) => {
    const question = questions[questionIndex];
    const option = question.options[optionIndex];
    
    // Check if option is locked (used in responses)
    const optionLockInfo = optionLocks[option.id];
    if (optionLockInfo && optionLockInfo.locked) {
      // Show dialog explaining why option can't be deleted
      setLockDialogInfo({
        question_text: `Option "${option.option_text}"`,
        answer_count: optionLockInfo.usage_count,
        is_option: true
      });
      setLockDialogOpen(true);
      return;
    }
    
    // Option not locked - proceed with deletion
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
    setQuestions(updatedQuestions);
    setIsDirty(true);
    scheduleLivePreview();
  };

  const scheduleLivePreview = () => {
    if (livePreviewTimerRef.current) clearTimeout(livePreviewTimerRef.current);
    livePreviewTimerRef.current = setTimeout(async () => {
      try {
        if (id) {
          setIsServerPreview(true);
          const html = await postSurveyLivePreview(
            {
              name: survey.name,
              description: survey.description,
              welcome_message: survey.welcome_message,
              thank_you_message: survey.thank_you_message,
            },
            questions
          );
          setSidePreviewHtml(html);
        } else {
          setIsServerPreview(false);
          setSidePreviewHtml(buildLivePreviewHtml(survey, questions));
        }
      } catch (e) {
        console.error('Live preview error:', e);
      }
    }, 400);
  };

  useEffect(() => {
    // Trigger preview when survey or questions are populated/changed
    if (id && questions.length >= 0 && survey.name !== undefined) {
      // For edit mode, wait until survey state is hydrated before generating preview
      scheduleLivePreview();
    } else if (!id) {
      // For new surveys, immediately show preview
      setSidePreviewHtml(buildLivePreviewHtml(survey, questions));
    }
    // eslint-disable-next-line
  }, [id, survey, questions]);

  useEffect(() => () => { if (livePreviewTimerRef.current) clearTimeout(livePreviewTimerRef.current); }, []);

  const handleSave = async (publish = false) => {
    if (isSaving) return; // prevent duplicate submissions
    try {
      setIsSaving(true);
      setSavingMode(publish ? 'publish' : 'draft');
      const surveyData = { ...survey, status: publish ? 'active' : 'draft' };
      
      let surveyId = id;
      if (id) {
        await updateSurvey(id, surveyData);
      } else {
        const result = await createSurvey(surveyData);
        surveyId = result.id;
      }

      // Note: Question deletions now happen immediately in handleDeleteQuestion,
      // not deferred until save. This ensures backend protections are checked upfront.

      // Save questions
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        const questionData = {
          question_text: question.question_text,
          question_type: question.question_type,
          is_required: question.is_required,
          display_order: i + 1,
          // Backend expects 'placeholder_text'
          placeholder_text: (question.placeholder ?? question.placeholder_text ?? null) || null,
          help_text: question.help_text || null,
          min_value: question.min_value || null,
          max_value: question.max_value || null,
          rating_scale: question.rating_scale || 5,
          options: question.options || [],
        };

        if (question.id && !question.id.toString().startsWith("temp-")) {
          await updateQuestion(question.id, questionData);
        } else {
          await addQuestion(surveyId, questionData);
        }
      }

  notify(publish ? "Survey published successfully!" : "Survey saved as draft!", { severity: 'success' });
      setIsDirty(false);
      // Refresh original IDs snapshot to reflect new persisted state
      originalQuestionIdsRef.current = (questions || [])
        .map(q => q.id)
        .filter(qid => qid && !qid.toString().startsWith('temp-'));
      history.push("/data-pulse");
    } catch (error) {
  console.error("Error saving survey:", error);
  notify("Error saving survey. Please try again.", { severity: 'error' });
    }
      setIsSaving(false);
      setSavingMode(null);
  };

  const renderQuestionPreview = (question, index) => {
    switch (question.question_type) {
      case "text":
      case "email":
      case "phone":
        return <TextField fullWidth placeholder={question.placeholder || "Your answer"} variant="outlined" size="small" />;
      
      case "textarea":
        return <TextField fullWidth multiline rows={4} placeholder={question.placeholder || "Your answer"} variant="outlined" />;
      
      case "multiple_choice":
        return (
          <RadioGroup>
            {question.options?.map((opt, i) => (
              <FormControlLabel key={i} value={opt.option_text} control={<Radio />} label={opt.option_text} />
            ))}
          </RadioGroup>
        );
      
      case "checkboxes":
        return (
          <Box>
            {question.options?.map((opt, i) => (
              <FormControlLabel key={i} control={<Checkbox />} label={opt.option_text} />
            ))}
          </Box>
        );
      
      case "dropdown":
        return (
          <Select fullWidth variant="outlined">
            {question.options?.map((opt, i) => (
              <MenuItem key={i} value={opt.option_text}>{opt.option_text}</MenuItem>
            ))}
          </Select>
        );
      
      case "rating":
        return <Rating value={0} max={question.rating_scale || 5} size="large" />;
      
      case "yes_no":
        return (
          <RadioGroup row>
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        );
      
      case "date":
        return <TextField type="date" fullWidth variant="outlined" InputLabelProps={{ shrink: true }} />;
      
      default:
        return <TextField fullWidth variant="outlined" size="small" />;
    }
  };

  const renderQuestionEditor = (question, index) => {
    const needsOptions = ["multiple_choice", "checkboxes", "dropdown"].includes(question.question_type);

    return (
      <Card 
        key={index} 
        className={classes.questionCard}
        draggable
        onDragStart={() => handleDragStart(index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragEnd={handleDragEnd}
        style={{ 
          opacity: draggedIndex === index ? 0.5 : 1,
          cursor: 'move'
        }}
      >
        <CardContent>
          <Box className={classes.questionHeader}>
            <DragIcon className={classes.dragHandle} style={{ cursor: 'grab' }} />
            <Typography variant="body2" color="textSecondary" style={{ marginRight: 16 }}>
              Q{index + 1}
            </Typography>
            <Tooltip title={questionLocks[question.id]?.locked ? `Question type locked - has ${questionLocks[question.id]?.answer_count} answer(s)` : ""}>
              <FormControl style={{ minWidth: 200, marginRight: 16 }}>
                <InputLabel>Question Type</InputLabel>
                <Select
                  value={question.question_type}
                  onChange={(e) => handleQuestionChange(index, "question_type", e.target.value)}
                  disabled={questionLocks[question.id]?.locked}
                >
                  {questionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
            <Box flexGrow={1} />
            <FormControlLabel
              control={
                <Switch
                  checked={question.is_required}
                  onChange={(e) => handleQuestionChange(index, "is_required", e.target.checked)}
                />
              }
              label="Required"
            />
            {questionLocks[question.id]?.locked ? (
              <Tooltip title={`Question has ${questionLocks[question.id]?.answer_count} answer(s) - cannot be deleted`}>
                <IconButton onClick={() => handleDeleteQuestion(index)} color="default">
                  <LockIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton onClick={() => handleDeleteQuestion(index)} color="secondary">
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <TextField
            fullWidth
            label="Question"
            value={question.question_text}
            onChange={(e) => handleQuestionChange(index, "question_text", e.target.value)}
            margin="normal"
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Help Text (Optional)"
            value={question.help_text || ""}
            onChange={(e) => handleQuestionChange(index, "help_text", e.target.value)}
            margin="normal"
            variant="outlined"
            size="small"
            helperText="Provide additional context or instructions for this question"
            multiline
            rows={2}
          />

          {(question.question_type === "text" || question.question_type === "textarea") && (
            <TextField
              fullWidth
              label="Placeholder"
              value={question.placeholder || ""}
              onChange={(e) => handleQuestionChange(index, "placeholder", e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
            />
          )}

          {question.question_type === "rating" && (
            <Box mt={2} mb={2}>
              <TextField
                type="number"
                label="Rating Scale"
                value={question.rating_scale || 5}
                onChange={(e) => handleQuestionChange(index, "rating_scale", parseInt(e.target.value))}
                variant="outlined"
                style={{ width: 200 }}
                inputProps={{ min: 1, max: 10 }}
                helperText="Set max rating (1-10)"
              />
            </Box>
          )}

          {needsOptions && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Options
              </Typography>
              {question.options?.map((option, optIndex) => (
                <Box key={optIndex} className={classes.option}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Option ${optIndex + 1}`}
                    value={option.option_text}
                    onChange={(e) => handleOptionChange(index, optIndex, e.target.value)}
                    variant="outlined"
                  />
                  {optionLocks[option.id]?.locked ? (
                    <Tooltip title={`Option used in ${optionLocks[option.id]?.usage_count} response(s) - cannot be deleted`}>
                      <IconButton onClick={() => handleDeleteOption(index, optIndex)} size="small">
                        <LockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <IconButton onClick={() => handleDeleteOption(index, optIndex)} size="small">
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<AddIcon />} onClick={() => handleAddOption(index)} size="small">
                Add Option
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderMainEditorContent = () => (
    <>
      {tabValue === 0 && (
        <Paper className={classes.paper}>
          <Typography variant="h6" gutterBottom>
            Survey Details
          </Typography>
          <TextField
            fullWidth
            label="Survey Name"
            value={survey.name}
            onChange={(e) => handleSurveyChange("name", e.target.value)}
            margin="normal"
            variant="outlined"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={survey.description}
            onChange={(e) => handleSurveyChange("description", e.target.value)}
            margin="normal"
            variant="outlined"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Welcome Message"
            value={survey.welcome_message}
            onChange={(e) => handleSurveyChange("welcome_message", e.target.value)}
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
            helperText="This message will be shown to respondents at the beginning of the survey"
          />
          <TextField
            fullWidth
            label="Thank You Message"
            value={survey.thank_you_message}
            onChange={(e) => handleSurveyChange("thank_you_message", e.target.value)}
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
            helperText="This message will be shown after survey completion"
          />
          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel>Link Expiration</InputLabel>
            <Select
              value={survey.nonce_expiration_hours !== undefined && survey.nonce_expiration_hours !== null ? survey.nonce_expiration_hours : 168}
              onChange={(e) => handleSurveyChange("nonce_expiration_hours", e.target.value)}
              label="Link Expiration"
            >
              <MenuItem value={24}>1 Day (24 hours)</MenuItem>
              <MenuItem value={120}>5 Days (120 hours)</MenuItem>
              <MenuItem value={168}>7 Days (168 hours) - Default</MenuItem>
              <MenuItem value={720}>30 Days (1 month)</MenuItem>
              <MenuItem value={0}>Unlimited (no expiration)</MenuItem>
            </Select>
            <Typography variant="caption" style={{ marginTop: 4, color: '#666' }}>
              How long respondents have to submit after opening the survey
            </Typography>
          </FormControl>
        </Paper>
      )}

      {tabValue === 1 && (
        <Box>
          {questions.map((question, index) => renderQuestionEditor(question, index))}
          
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddQuestion}
            fullWidth
            style={{ marginTop: 16, padding: 16 }}
          >
            Add Question
          </Button>
        </Box>
      )}
    </>
  );

  return (
    <Box className={classes.root}>
      {/* Navigation guard handled via history.block and beforeunload listeners */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton 
            onClick={() => history.push('/data-pulse')} 
            style={{ marginRight: 8 }}
            title="Back to Surveys"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">{id ? "Edit Survey" : "Create New Survey"}</Typography>
        </Box>
        <Box>
          <Button
            startIcon={<PreviewIcon />}
            onClick={() => {
              if (isSmall) {
                setPreviewVisibleMobile(v => !v);
              } else {
                setPreviewOpen(true);
              }
            }}
            style={{ marginRight: 8 }}
            disabled={isSaving}
          >
            {isSmall ? (previewVisibleMobile ? 'Hide Preview' : 'Show Preview') : 'Preview'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleSave(false)}
            style={{ marginRight: 8 }}
            disabled={isSaving}
          >
            {savingMode === 'draft' && isSaving ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={() => handleSave(true)}
            disabled={isSaving}
          >
            {savingMode === 'publish' && isSaving ? 'Publishing…' : 'Publish'}
          </Button>
        </Box>
      </Box>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} style={{ marginBottom: 24 }}>
        <Tab label="Survey Details" />
        <Tab label="Questions" />
      </Tabs>

      {!isSmall ? (
      <Box display="flex" alignItems="flex-start" width="100%">
        <Box flexGrow={1} pr={2}>
          {renderMainEditorContent()}
        </Box>
  <Box width={560} flexShrink={0} position="sticky" top={16}>
          <Paper style={{ height: 'calc(100vh - 120px)', padding: 0, display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <Box p={1.5} borderBottom="1px solid #e0e0e0" display="flex" alignItems="center" justifyContent="space-between" style={{ background: '#fafafa' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Live Preview</Typography>
              <Chip size="small" label={id ? (isServerPreview ? 'Server' : 'Draft') : 'Draft (new)'} />
            </Box>
            <Box flexGrow={1} style={{ background: '#f5f5f5' }}>
              <iframe
                title="side-live-preview"
                srcDoc={sidePreviewHtml}
                sandbox="allow-scripts allow-forms"
                style={{ width: '100%', height: '100%', border: 0, background: 'transparent' }}
              />
            </Box>
          </Paper>
        </Box>
      </Box>
      ) : (
        <Box>
          {renderMainEditorContent()}
        </Box>
      )}

      {isSmall && previewVisibleMobile && (
        <Box mt={2}>
          <Paper style={{ height: 520, padding: 0, display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <Box p={1.5} borderBottom="1px solid #e0e0e0" display="flex" alignItems="center" justifyContent="space-between" style={{ background: '#fafafa' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Live Preview</Typography>
              <Chip size="small" label={id ? (isServerPreview ? 'Server' : 'Draft') : 'Draft (new)'} />
            </Box>
            <Box flexGrow={1} style={{ background: '#f5f5f5' }}>
              <iframe
                title="mobile-live-preview"
                srcDoc={sidePreviewHtml}
                sandbox="allow-scripts allow-forms"
                style={{ width: '100%', height: '100%', border: 0, background: 'transparent' }}
              />
            </Box>
          </Paper>
        </Box>
      )}

      {/* Preview Dialog */}
  <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Survey Preview
          <IconButton
            onClick={() => setPreviewOpen(false)}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
          <Box style={{ width: '100%', height: '80vh' }}>
            <iframe
              title="survey-preview"
              srcDoc={sidePreviewHtml}
              sandbox="allow-scripts allow-forms"
              style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Lock Dialog */}
      <Dialog open={lockDialogOpen} onClose={() => setLockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {lockDialogInfo?.is_option ? 'Option Cannot Be Deleted' : 'Question Cannot Be Deleted'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {lockDialogInfo?.is_option ? (
              <>
                <strong>{lockDialogInfo?.question_text}</strong> has been selected in <strong>{lockDialogInfo?.answer_count} response(s)</strong>.
              </>
            ) : (
              <>
                This question has <strong>{lockDialogInfo?.answer_count} answer(s)</strong> from survey respondents.
              </>
            )}
          </Typography>
          <Typography gutterBottom style={{ marginTop: 16 }}>
            Deleting it would cause permanent data loss. Instead, you can:
          </Typography>
          <ul style={{ marginTop: 8, paddingLeft: 24 }}>
            <li>
              <Typography variant="body2">
                <strong>Duplicate this survey</strong> to create an editable copy without responses
              </Typography>
            </li>
            {!lockDialogInfo?.is_option && (
              <li>
                <Typography variant="body2">
                  Update the question text (safe - doesn't affect existing data)
                </Typography>
              </li>
            )}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateSurvey;
